<script>
import { isLatLngValid, mapHKAdminRegion } from '../../libs/location'
import _get from 'lodash/get'
import { getEmploymentTypeLocaleKey } from '../../libs/mapping/employmentType'
import { getPageMetadata } from '../../libs/metadata'
import JobMixin from '../../mixins/job'

/**
 * Map employment type to schema.org format
 */
const empTypeMapping = {
  FullTime: 'FULL_TIME',
  PartTime: 'PART_TIME',
  Temp: 'TEMPORARY',
}

export default {
  computed: {
    companyNameLabel() {
      return _get(this.job, 'company.name') || ''
    },
    companyShareImage() {
      return _get(this.job, 'company.company_logo_medium') || ''
    },
    employmentTypeLabel() {
      return this.$t(getEmploymentTypeLocaleKey(this.employmentType)) || ''
    },
    /**
     * Description
     */
    metaDescription() {
      const separator = this.$t('general.comma')
      const workingHours = this.workingHourRange
        ? `${separator}${this.$tc('jobDetail.hoursPerDayFull', this.workingHourRange, {
            hours: this.workingHourRange,
          })}`
        : ''
      return this.$t('jobDetail.description', {
        district: this.districtsLabel,
        employerName: this.companyNameLabel,
        employmentType: this.employmentTypeLabel,
        jobTitle: this.jobName,
        jobType: this.jobTypesLabel,
        salary: this.salaryLabel,
        workingDays: this.$tc('jobDetail.daysPerWeekFull', this.workingDayRange, {
          days: this.workingDayRange,
        }),
        workingHours,
      })
    },
    /**
     * Share image:
     * 1) Company logo
     */
    metaImages() {
      const images = []
      if (this.companyShareImage) {
        images.push(this.companyShareImage)
      }
      return images
    },
    /**
     * Title
     */
    metaTitle() {
      return this.$t('jobDetail.title', {
        district: this.districtsLabel,
        employerName: this.companyNameLabel,
        employmentType: this.employmentTypeLabel,
        jobTitle: this.jobName,
      })
    },
    salaryLabel() {
      // Return either hourly or monthly salary
      if (this.hourlySalaryRange) {
        return this.$t('jobDetail.metaSalaryPerHour', {
          number: this.hourlySalaryRange,
        })
      } else if (this.monthlySalaryRange) {
        return this.$t('jobDetail.metaSalaryPerMonth', {
          number: this.monthlySalaryRange,
        })
      }
      return ''
    },
    /**
     * schema.org MonetaryAmount entity for base salary
     */
    schemaBaseSalary() {
      // Determine salary unit
      let salaryUnit = ''
      if (this.job.from_hourly_rate) {
        salaryUnit = 'hour'
      } else if (this.job.from_monthly_rate) {
        salaryUnit = 'month'
      }

      // Return salary entity if value available
      if (salaryUnit) {
        const value = {
          '@type': 'QuantitativeValue',
          unitText: salaryUnit.toUpperCase(),
        }

        if (this.job[`to_${salaryUnit}ly_rate`]) {
          // Salary range
          value.minValue = this.job[`from_${salaryUnit}ly_rate`]
          value.maxValue = this.job[`to_${salaryUnit}ly_rate`]
        } else {
          // Salary base only
          value.value = this.job[`from_${salaryUnit}ly_rate`]
        }

        return {
          '@type': 'MonetaryAmount',
          currency: 'HKD',
          value,
        }
      }

      return null
    },
    /**
     * schema.org Organization entity for hiring organization
     */
    schemaHiringOrg() {
      if (this.companyNameLabel) {
        return {
          '@type': 'Organization',
          logo: this.companyShareImage,
          name: this.companyNameLabel,
        }
      }
      return null
    },
    /**
     * schema.org Place entities for job locations
     */
    schemaJobLocations() {
      if (Array.isArray(this.job.address) && this.job.address.length) {
        return this.job.address
          .filter((address) => address && address.address)
          .map((address) => {
            const location = {
              '@type': 'Place',
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'HK',
                postalCode: '00000',
                streetAddress: address.address,
              },
            }

            // Add location district info if available
            if (address.district_name) {
              location.address.addressLocality = address.district_name
              location.address.addressRegion = mapHKAdminRegion[address.district_name] || address.district_name
            }

            // Add location latlng if available
            if (isLatLngValid(address.lat, address.lng)) {
              location.geo = {
                '@type': 'GeoCoordinates',
                latitude: address.lat,
                longitude: address.lng,
              }
            }

            return location
          })
      }
      return []
    },
    /**
     * schema.org JobPosting entity,
     * ref: https://developers.google.com/search/docs/data-types/job-posting
     */
    schemaJobPosting() {
      const posting = {
        '@context': 'https://schema.org/',
        '@type': 'JobPosting',
        description: this.jobDescription,
        mainEntityOfPage: `${process.env.SEEKER_WEB__BASE_URL}${this.$route.path}`,
        title: this.jobName,
      }

      // Add fields if available
      if (this.job.published_at && this.job.published_at.length >= 10) {
        // Date format: YYYY-MM-DD,
        // => first 10 characters in JS date string
        posting.datePosted = this.job.published_at.substr(0, 10)
      }
      if (empTypeMapping[this.employmentType]) {
        posting.employmentType = empTypeMapping[this.employmentType]
      }

      // Add children entities
      if (this.schemaHiringOrg) {
        posting.hiringOrganization = this.schemaHiringOrg
      }
      if (this.schemaBaseSalary) {
        posting.baseSalary = this.schemaBaseSalary
      }
      if (this.schemaJobLocations.length) {
        posting.jobLocation = this.schemaJobLocations
      }

      return posting
    },
    vacancyLabel() {
      if (this.vacancy >= 0) {
        return this.$tc('jobDetail.vacancy', this.vacancy)
      }
      return ''
    },
  },
  head() {
    return {
      ...getPageMetadata(this.metaTitle, this.metaDescription, this.metaImages),
      script: [
        {
          hid: 'job-posting',
          json: this.schemaJobPosting,
          type: 'application/ld+json',
        },
      ],
    }
  },
  mixins: [JobMixin],
  // Handle to empty single root
  render(createElement) {
    return null
  },
}
</script>
